/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import Vue from 'vue';
import { ActionContext, Module } from 'vuex';
import { extend, cloneDeep, isEqual, find } from 'lodash';
import { DEFAULT_FILTERS, OPTION_ALL, OPTION_NONE, SelectedFilters, Filters } from './helpers';
import { RootState } from '@/state/types';

interface State {
  loading: object;
  selectedScore: object | null;
  selectedVectorX: object;
  selectedVectorY: object;
  filters: Filters;
  selectedFilters: SelectedFilters;
}

function discardInvalidFilters(selectedFilters: any, filters: any) {
  // Some objects/tags/... might have been removed after filters were stored
  //   into a local storage. We have to discard such invalid filters:
  for (const key in selectedFilters) {
    if (!Object.prototype.hasOwnProperty.call(selectedFilters, key)) continue;

    const correspondingKeysDict = {
      objectType: 'objectTypes',
      objectId: 'filterObjects',
    };
    const correspondingKey = correspondingKeysDict[key] || key;

    if (Array.isArray(selectedFilters[key])) {
      selectedFilters[key] = selectedFilters[key].filter(f => find(filters[correspondingKey] || [], { value: f }));
    } else if (!find(filters[correspondingKey] || [], { value: selectedFilters[key] })) {
      delete selectedFilters[key];
    }
  }
}

const state: State = {
  loading: {
    filters: false,
    filterObjects: false,
  },
  selectedScore: null,
  selectedVectorX: {},
  selectedVectorY: {},

  filters: {
    objectTypes: [],
    statuses: [],
    tags: [],
    owners: [],
    filterObjects: [],
  },
  selectedFilters: cloneDeep(DEFAULT_FILTERS),
};

export const getters = {
  loading: (state: State): Object => state.loading,
  isFilteringByObjectType: (state: State): boolean =>
    Boolean(state.selectedFilters.objectType && state.filters.objectTypes.length),

  selectedScore: (state: State): Object | null => state.selectedScore,
  selectedVectorX: (state: State): Object => state.selectedVectorX,
  selectedVectorY: (state: State): Object => state.selectedVectorY,

  filters: (state: State): Object => state.filters,

  selectedFilters: (state: State): Object => state.selectedFilters,

  notDefaultSelectedFilters(state: State): Boolean {
    return !isEqual(state.selectedFilters, DEFAULT_FILTERS);
  },
};

export const mutations = {
  SET_LOADING(state: State, params: object) {
    extend(state.loading, params);
  },
  SET_SELECTED_SCORE: (state: State, selectedScore: object) => (state.selectedScore = cloneDeep(selectedScore)),
  SET_SELECTED_VECTOR_X: (state: State, selectedVectorX: object) =>
    (state.selectedVectorX = cloneDeep(selectedVectorX)),
  SET_SELECTED_VECTOR_Y: (state: State, selectedVectorY: object) =>
    (state.selectedVectorY = cloneDeep(selectedVectorY)),

  SET_FILTERS(state: State, filters: object) {
    Vue.set(state, 'filters', filters);
  },
  SET_FILTER_OBJECTS(state: State, filterObjects: object[]) {
    filterObjects.unshift(OPTION_ALL);
    Vue.set(state.filters, 'filterObjects', filterObjects);
  },
  SET_SELECTED_FILTERS(state: State, selectedFilters: object) {
    extend(state.selectedFilters, selectedFilters);
  },
};

export const actions = {
  async fetchFilters({ commit }: ActionContext) {
    const fixFilters = (filters: Filters) => {
      filters.objectTypes.unshift(OPTION_ALL);
      filters.tags.unshift(OPTION_NONE);
      filters.owners = filters.owners.map(({ email, name, role }) => ({
        id: email,
        label: name,
        value: email,
        role,
      }));
    };

    commit('SET_LOADING', { filters: true });
    const filters = await this.dispatch('request', {
      route: 'riskHeatmapFilters',
    });
    fixFilters(filters);
    commit('SET_FILTERS', filters);
    commit('SET_LOADING', { filters: false });
  },
  async fetchFilterObjects({ commit, getters }: ActionContext) {
    commit('SET_LOADING', { filterObjects: true });
    let filterObjects = await this.dispatch('request', {
      route: 'riskMappedObjectTargets',
      params: {
        objectName: getters.selectedFilters.objectType,
        xVectorId: getters.selectedVectorX.id,
        yVectorId: getters.selectedVectorY.id,
        scoreId: getters.selectedScore.id,
      },
    });
    commit('SET_FILTER_OBJECTS', filterObjects);
    commit('SET_SELECTED_FILTERS', { objectId: OPTION_ALL.value });
    commit('SET_LOADING', { filterObjects: false });
  },
  setScore({ commit }: ActionContext, selectedScore: Object) {
    commit('SET_SELECTED_SCORE', selectedScore);
  },
  setVectorX({ commit }: ActionContext, selectedVectorX: Object) {
    commit('SET_SELECTED_VECTOR_X', selectedVectorX);
  },
  setVectorY({ commit }: ActionContext, selectedVectorY: Object) {
    commit('SET_SELECTED_VECTOR_Y', selectedVectorY);
  },
  setFilters({ commit, getters, dispatch }: ActionContext, filters: SelectedFilters) {
    let objectTypeChanged =
      filters.hasOwnProperty('objectType') && getters.selectedFilters.objectType !== filters.objectType;

    discardInvalidFilters(filters, getters.filters);

    commit('SET_SELECTED_FILTERS', filters);

    if (objectTypeChanged) {
      if (!filters.objectType) {
        commit('SET_FILTER_OBJECTS', [cloneDeep(OPTION_ALL)]);
        commit('SET_SELECTED_FILTERS', { objectId: OPTION_ALL.value });
      } else {
        dispatch('fetchFilterObjects');
      }
    }
  },
  resetFilters({ commit }: ActionContext) {
    commit('SET_SELECTED_FILTERS', cloneDeep(DEFAULT_FILTERS));
  },
};

export default (): Module<State, RootState> => ({
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
});
